import type { CSSProperties } from 'react';
import { neonVar } from '@/utils/neon-utils';
/* ═══════════════════════════════════════════════════════   Design tokens   ═══════════════════════════════════════════════════════ */ export const fontFamily =
  neonVar('font-family', 'Roboto, Arial, Helvetica, sans-serif');

/* ── Icon size constants ── */
export const ICON_SIZE_SM = 16;
export const ICON_SIZE_MD = 18;
export const ICON_SIZE_DEFAULT = 20;
export const ICON_SIZE_LG = 24;
export const FILTER_ICON_SIZE = 20;
export const CARET_ICON_SIZE = 24;

export const COLOR = {
  textPrimary: '#333333',
  textSecondary: '#4D5557',
  textMuted: '#636E70',
  textGray: '#6B7280',
  textDark: '#111827',
  textBody: '#374151',
  linkBlue: '#1162EE',
  navyDark: '#00427C',
  border: '#E5E7EB',
  borderMedium: '#C4C9CA',
  progressTrack: '#C4C9CA',
  progressTrackBorder: '#A0A5A8',
  progressFill: '#2563EB',
  white: '#FFFFFF',
  pageBg: '#ECEFF2',
  statusBorder: '#A8B1B2',
  statusBg: '#F1F4F4',
  statusText: '#4D5557',
  completedBg: neonVar('color-green-100', '#E4F7DE'), // Green 100
  completedText: neonVar('color-green-700', '#134D00'), // Green 700
  inProgressBg: neonVar('color-navy-100', '#E8F0FD'), // Navy 100
  inProgressText: neonVar('color-navy-700', '#00427C'), // Navy 700
  planningBg: neonVar('color-purple-100', '#E9DDF5'), // Purple 100
  planningText: neonVar('color-purple-700', '#5C367F'), // Purple 700
  notOfferedBg: '#FDF4D1',
  notOfferedText: '#614E00',
  incompleteBg: neonVar('color-red-100', '#FBDDDA'), // Red 100
  incompleteText: neonVar('color-red-700', '#930C0C'), // Red 700
  currentBadgeBg: '#E8F0FD',
  currentBadgeText: '#00427C',
  countBadgeBg: '#1162EE',
  alternateBg: '#F1F4F4',
  sectionDivider: '#D1D5DB',
  arrowEnabled: '#6B7280',
  arrowDisabled: '#D1D5DB',
  /* Alert colours (matching prototype AlertBanner / CourseCard alert) */
  alertBannerBg: '#FDF4D1',
  alertBannerBorder: '#F9CE33',
  alertBannerText: '#614E00',
  alertCourseBg: '#FDF4D1',
  alertCourseText: '#614E00',
  alertFlagColor: '#EA580C',
  alertRequirementFlagColor: '#614E00',
  /* Filter status icon colours (matching prototype StatusFilterDropdown) */
  filterAllStroke: neonVar('color-gray-700', '#4D5557'), // Gray 700
  filterCompletedFill: neonVar('color-green-500', '#218900'), // Green 500
  filterInProgressStroke: neonVar('color-navy-500', '#1162EE'), // Navy 500
  filterPlannedStroke: neonVar('color-purple-500', '#9352D0'), // Purple 500
  filterNotOfferedStroke: '#614E00',
  filterRemainingStroke: neonVar('color-gray-600', '#677274'), // Gray 600 (closest to prototype #636E70)
  filterIncompleteFill: neonVar('color-red-500', '#E21707'), // Red 500
  filterCaretFill: neonVar('color-gray-700', '#4D5557'), // Gray 700
  /* Filter option hover / selected background */
  filterOptionSelectedBg: '#EFF6FF',
  filterOptionHoverBg: '#F9FAFB',
  /* Credit dropdown "not planned" highlight */
  creditNotPlannedBg: '#FEF3C7',
  creditNotPlannedText: '#92400E',
  /* Drawer card subtitle */
  drawerCardSubtitleText: '#666666',
  /* Course card selected state */
  selectedCardBorder: neonVar('color-navy-700', '#00427C'), // Navy 700
  selectedCardBg: '#F5F9FF',
  /* In-plan status badge colours */
  inPlanCompletedText: neonVar('color-green-600', '#16A34A'), // Green 600
  inPlanInProgressText: neonVar('color-navy-500', '#1162EE'), // Navy 500
  /* Grade selector selected-state check circle */
  selectionCheckGreen: neonVar('color-green-500', '#22C55E'), // Green 500
} as const;
/* ═══════════════════════════════════════════════════════   Page layout   ═══════════════════════════════════════════════════════ */ export const pageWrapperStyle: CSSProperties =
  {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    fontFamily,
  };
export const headerZoneStyle: CSSProperties = { flexShrink: 0 };
export const mainCardStyle: CSSProperties = {
  flex: 1,
  margin: '4px 8px 8px',
  backgroundColor: COLOR.white,
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};
export const scrollableGridStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  minHeight: 0,
  paddingBottom: 88,
};
/* ═══════════════════════════════════════════════════════   Breadcrumbs   ═══════════════════════════════════════════════════════ */ export const breadcrumbNavStyle: CSSProperties =
  {
    fontFamily,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: COLOR.textGray,
    padding: '4px 16px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
export const breadcrumbLinkStyle: CSSProperties = {
  color: 'inherit',
  textDecoration: 'none',
  cursor: 'pointer',
};
export const breadcrumbCurrentStyle: CSSProperties = { color: COLOR.textDark };
/* ═══════════════════════════════════════════════════════   Page header   ═══════════════════════════════════════════════════════ */ export const pageHeaderContainerStyle: CSSProperties =
  { padding: '8px 16px 10px' };
export const pageHeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
};
export const pageHeaderLeftStyle: CSSProperties = { display: 'flex', flexDirection: 'column' };
export const titleRowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
export const titleStyle: CSSProperties = {
  fontFamily,
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '32px',
  color: COLOR.textPrimary,
  margin: 0,
};
export const infoButtonStyle: CSSProperties = {
  padding: 4,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderRadius: 4,
  color: COLOR.textMuted,
  display: 'flex',
};
export const subtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  color: COLOR.textSecondary,
  margin: '2px 0 0',
};
export const statusBadgeStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  lineHeight: '18px',
  fontWeight: 400,
  padding: '3px 8px',
  borderRadius: '100px',
  border: `1px solid ${COLOR.statusBorder}`,
  backgroundColor: COLOR.statusBg,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};
export const statusLabelStyle: CSSProperties = { color: COLOR.statusText, fontWeight: 500 };
export const statusValueStyle: CSSProperties = { color: COLOR.statusText };
export const toolbarContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  flexShrink: 0,
};
export const creditProgressButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 8px',
  borderRadius: 4,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};
export const progressTrackStyle: CSSProperties = {
  width: 120,
  height: 8,
  borderRadius: '9999px',
  overflow: 'hidden',
  backgroundColor: COLOR.progressTrack,
  border: `1px solid ${COLOR.progressTrackBorder}`,
};
export const progressFillStyle = (pct: number): CSSProperties => ({
  width: `${pct}%`,
  height: '100%',
  backgroundColor: COLOR.progressFill,
  transition: 'width 0.3s',
});
export const creditTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: COLOR.textBody,
  whiteSpace: 'nowrap',
};
export const toolbarButtonStyle: CSSProperties = {
  height: 32,
  padding: '0 8px',
  backgroundColor: COLOR.white,
  border: `1px solid ${COLOR.borderMedium}`,
  borderRadius: 4,
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  color: COLOR.navyDark,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
};
/* ═══════════════════════════════════════════════════════   Count badge   ═══════════════════════════════════════════════════════ */ export const countBadgeStyle: CSSProperties =
  {
    backgroundColor: COLOR.countBadgeBg,
    color: COLOR.white,
    borderRadius: '9999px',
    minWidth: 18,
    height: 18,
    padding: '0 4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily,
    fontSize: '12px',
    fontWeight: 400,
  };
/* ═══════════════════════════════════════════════════════   Current grade badge   ═══════════════════════════════════════════════════════ */ export const currentBadgeStyle: CSSProperties =
  {
    backgroundColor: COLOR.currentBadgeBg,
    color: COLOR.currentBadgeText,
    fontFamily,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '18px',
    padding: '3px 8px',
    borderRadius: '9999px',
  };
/* ═══════════════════════════════════════════════════════   StatusIcon   ═══════════════════════════════════════════════════════ */ export const statusIconContainerStyle: CSSProperties =
  { display: 'flex', alignItems: 'center', gap: 2 };
export const statusIconWrapperStyle: CSSProperties = {
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
export const gradeAwardedStyle = (color: string): CSSProperties => ({
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color,
});
/* ═══════════════════════════════════════════════════════   Course card   ═══════════════════════════════════════════════════════ */ export const courseCardStyle =
  (bg: string): CSSProperties => ({
    width: '100%',
    padding: 16,
    borderRadius: 8,
    backgroundColor: bg,
    position: 'relative',
    boxSizing: 'border-box',
    overflow: 'hidden',
  });
export const courseCardRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: 0,
};
export const courseCardTitleStyle = (color: string): CSSProperties => ({
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
});
export const courseCardActionsStyle: CSSProperties = {
  marginLeft: 8,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
export const iconButtonStyle = (color: string): CSSProperties => ({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color,
  flexShrink: 0,
});
export const courseCardSubtitleStyle = (color: string): CSSProperties => ({
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  color,
  margin: '2px 0 0',
});
export const courseCardNoteStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '0 8px',
  marginTop: 8,
};
export const courseCardNoteTextStyle = (color: string): CSSProperties => ({
  fontFamily,
  fontSize: '12px',
  color,
});

/* Metadata line (specialization · alternates) below subtitle */
export const courseCardMetaLineStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 4,
  fontFamily,
  fontSize: '12px',
  color: COLOR.textMuted,
};

/* Alert message row displayed inside course card (flag icon + text) */
export const courseCardAlertRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  marginTop: 8,
};
export const courseCardAlertTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '18px',
  color: COLOR.alertCourseText,
};
/* ═══════════════════════════════════════════════════════   Add slot (dashed card)   ═══════════════════════════════════════════════════════ */ export const addSlotStyle: CSSProperties =
  {
    width: '100%',
    padding: 16,
    backgroundColor: COLOR.white,
    borderRadius: 8,
    border: `1px dashed ${COLOR.borderMedium}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    overflow: 'hidden',
  };
export const addSlotLabelStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  color: COLOR.textMuted,
};

/* ═══════════════════════════════════════════════════════
   Requirement met card (shown when all credits are satisfied)
   ═══════════════════════════════════════════════════════ */
export const requirementMetCardStyle: CSSProperties = {
  width: '100%',
  padding: 16,
  backgroundColor: COLOR.white,
  borderRadius: 8,
  border: `1px solid ${COLOR.border}`,
  boxSizing: 'border-box',
};
export const requirementMetTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: '0 0 4px',
};
export const requirementMetSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '13px',
  fontWeight: 400,
  color: COLOR.textMuted,
  margin: 0,
};

/* ═══════════════════════════════════════════════════════   Requirement cell (left column)   ═══════════════════════════════════════════════════════ */ export const requirementCellStyle: CSSProperties =
  {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: '100%',
    backgroundColor: COLOR.white,
  };
export const requirementCellInnerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
export const requirementLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.linkBlue,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  textAlign: 'left',
};

export const requirementCreditsStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  color: COLOR.textGray,
};

export const progressBarTrackStyle: CSSProperties = {
  width: '100%',
  height: 8,
  backgroundColor: COLOR.border,
  borderRadius: '9999px',
  overflow: 'hidden',
};

export const progressBarFillStyle = (pct: number): CSSProperties => ({
  height: '100%',
  width: `${pct}%`,
  backgroundColor: COLOR.progressFill,
  borderRadius: '9999px',
  transition: 'width 0.3s',
});

/* ═══════════════════════════════════════════════════════
   Grade header cell
   ═══════════════════════════════════════════════════════ */

export const gradeHeaderCellStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  borderRight: `1px solid ${COLOR.border}`,
  padding: 16,
  backgroundColor: COLOR.white,
};

export const gradeHeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const gradeHeaderLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

export const gradeHeaderTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: 0,
};

export const gradeHeaderCreditsStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: COLOR.textGray,
};

/* ═══════════════════════════════════════════════════════
   Grade course cell
   ═══════════════════════════════════════════════════════ */

export const gradeCourseCellStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  borderRight: `1px solid ${COLOR.border}`,
  padding: 16,
  backgroundColor: COLOR.white,
  overflow: 'hidden',
  boxSizing: 'border-box',
};

export const gradeCourseCellInnerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 0,
};

/* ═══════════════════════════════════════════════════════
   Alternate card (grey)
   ═══════════════════════════════════════════════════════ */

export const alternateCardStyle: CSSProperties = {
  width: '100%',
  padding: 16,
  borderRadius: 8,
  backgroundColor: COLOR.alternateBg,
  boxSizing: 'border-box',
  overflow: 'hidden',
};

export const alternateCardTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.textSecondary,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
};

export const alternateCardSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  color: COLOR.textSecondary,
  margin: '2px 0 0',
};

/* ═══════════════════════════════════════════════════════
   Alternate 1:1 card — dashed border, white background (per prototype)
   ═══════════════════════════════════════════════════════ */

export const alt1to1CardStyle: CSSProperties = {
  width: '100%',
  padding: 16,
  borderRadius: 12,
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

/** Applied when card has alternates — solid gray bg, no border */
export const alt1to1CardWithAltsStyle: CSSProperties = {
  ...alt1to1CardStyle,
  backgroundColor: '#F1F4F4',
};

/** Applied when card has NO alternates — white bg with dashed border */
export const alt1to1CardEmptyStyle: CSSProperties = {
  ...alt1to1CardStyle,
  backgroundColor: '#FFFFFF',
  border: '1px dashed #C4C9CA',
};

export const alt1to1HeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
};

export const alt1to1CourseNameStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

/** Course name color when card has alternates */
export const ALT1TO1_NAME_COLOR_WITH_ALTS = '#4D5557';
/** Course name color when card has no alternates */
export const ALT1TO1_NAME_COLOR_EMPTY = '#636E70';

export const alt1to1LabelStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  lineHeight: '18px',
  color: '#4D5557',
};

export const alt1to1ActionLinkStyle: CSSProperties = {
  marginTop: 8,
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  color: '#00427C',
  textDecoration: 'none',
  width: 'fit-content',
};

export const alt1to1SwapActionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  color: '#00427C',
  textDecoration: 'none',
  flexShrink: 0,
  minHeight: 44,
  minWidth: 44,
};

export const alt1to1AlternateRowStyle: CSSProperties = {
  marginTop: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  minWidth: 0,
  backgroundColor: '#FFFFFF',
  border: '1px solid #C4C9CA',
  borderRadius: 12,
  padding: '8px',
};

export const alt1to1AlternateNameWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
  flex: 1,
};

export const alt1to1AlternateNumberStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '1px solid #C4C9CA',
  fontFamily,
  fontSize: '12px',
  color: '#4D5557',
  flexShrink: 0,
};

export const alt1to1AlternateNameStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: '#333333',
  lineHeight: '20px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const alt1to1ColumnHeaderStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: 12,
};

/* ═══════════════════════════════════════════════════════
   Section divider (thick grey bar between sections)
   ═══════════════════════════════════════════════════════ */

export const sectionDividerStyle: CSSProperties = {
  height: 4,
  backgroundColor: COLOR.sectionDivider,
  flexShrink: 0,
};

/* ═══════════════════════════════════════════════════════
   Alternates left cell
   ═══════════════════════════════════════════════════════ */

export const alternatesLeftCellStyle: CSSProperties = {
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: '100%',
  backgroundColor: COLOR.white,
};

export const alternatesLeftCellInnerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const alternatesSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  color: COLOR.textGray,
};

/* ═══════════════════════════════════════════════════════
   Credits-in-plan footer & out-of-plan left cell
   ═══════════════════════════════════════════════════════ */

export const creditsInPlanFooterCellStyle: CSSProperties = {
  padding: 16,
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  backgroundColor: COLOR.white,
  borderTop: `1px solid ${COLOR.border}`,
};

export const creditsInPlanFooterTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: 0,
};

export const creditsInPlanFooterGradeCellStyle: CSSProperties = {
  ...gradeCourseCellStyle,
  borderTop: `1px solid ${COLOR.border}`,
  justifyContent: 'center',
  alignItems: 'center',
};

export const creditsInPlanFooterGradeTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
};

export const outOfPlanLeftCellStyle: CSSProperties = {
  ...alternatesLeftCellStyle,
  borderTop: `1px solid ${COLOR.border}`,
};

export const outOfPlanTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: 0,
};

export const outOfPlanDescriptionStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  color: COLOR.textGray,
  margin: 0,
  lineHeight: '16px',
};

export const outOfPlanCreditsNoteStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 600,
  color: COLOR.textGray,
  margin: 0,
};

export const noAlternatesTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: COLOR.textGray,
};

/* ═══════════════════════════════════════════════════════
   Footer
   ═══════════════════════════════════════════════════════ */

export const footerStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  borderTop: `1px solid ${COLOR.border}`,
  padding: 16,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 16,
  backgroundColor: COLOR.white,
  flexShrink: 0,
  zIndex: 20,
  boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
};

/* ═══════════════════════════════════════════════════════
   ScrollRow
   ═══════════════════════════════════════════════════════ */

export const scrollRowStyle = (isLastRow?: boolean): CSSProperties => ({
  display: 'flex',
  borderBottom: isLastRow ? 'none' : `1px solid ${COLOR.border}`,
  alignItems: 'stretch',
});

export const scrollRowLeftStyle: CSSProperties = {
  width: 220,
  flexShrink: 0,
  borderRight: `1px solid ${COLOR.border}`,
  backgroundColor: COLOR.white,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

export const scrollRowRightStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

export const scrollRowSliderStyle = (pageIndex: 0 | 1): CSSProperties => ({
  display: 'flex',
  transition: 'transform 300ms ease-in-out',
  width: '200%',
  transform: `translateX(${pageIndex * -50}%)`,
  minHeight: '100%',
});

export const scrollRowPanelStyle: CSSProperties = {
  width: '50%',
  flexShrink: 0,
  display: 'flex',
  minHeight: '100%',
};

/* ── Grade strip slider (7 columns, 4 visible at a time) ── */

/** Total number of grade columns in the strip */
const TOTAL_GRADE_COLUMNS = 7;
/** Number of grade columns visible at any time */
const VISIBLE_COLUMNS = 4;

/**
 * Container for all 7 grade columns laid out in a flex row.
 * Width is (TOTAL / VISIBLE) × 100% so each column naturally
 * occupies 1/VISIBLE of the visible viewport.
 * translateX shifts by (offset / TOTAL) × 100% per step.
 */
export const gradeStripSliderStyle = (offset: number): CSSProperties => ({
  display: 'flex',
  transition: 'transform 300ms ease-in-out',
  width: `${(TOTAL_GRADE_COLUMNS / VISIBLE_COLUMNS) * 100}%`,
  transform: `translateX(${(-offset / TOTAL_GRADE_COLUMNS) * 100}%)`,
  minHeight: '100%',
});

/**
 * Each individual grade column inside the strip.
 * flex: 1 so all 7 share the strip width equally.
 */
export const gradeColumnStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  minHeight: '100%',
};

export const gradePanelFlexStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
};

export const gradePanelFlexWhiteStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  backgroundColor: COLOR.white,
};

/* ═══════════════════════════════════════════════════════
   Header row left content
   ═══════════════════════════════════════════════════════ */

export const headerLeftContentStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: 16,
  backgroundColor: COLOR.white,
  position: 'sticky',
  top: 0,
  zIndex: 10,
  height: '100%',
};

export const requirementsHeadingStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: 0,
};

export const arrowContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};

export const arrowButtonStyle = (enabled: boolean): CSSProperties => ({
  padding: 4,
  background: 'none',
  border: 'none',
  borderRadius: 4,
  display: 'flex',
  cursor: enabled ? 'pointer' : 'not-allowed',
  color: enabled ? COLOR.arrowEnabled : COLOR.arrowDisabled,
});

/* ── Plan Description Modal ── */

export const modalOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

export const modalContainerStyle: CSSProperties = {
  backgroundColor: COLOR.white,
  borderRadius: 8,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  maxWidth: 512,
  width: '100%',
  margin: '0 16px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
};

export const modalHeaderStyle: CSSProperties = {
  padding: '16px',
  borderBottom: `1px solid ${COLOR.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const modalTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  fontWeight: 500,
  color: COLOR.textPrimary,
  margin: 0,
};

export const modalCloseButtonStyle: CSSProperties = {
  padding: 4,
  background: 'none',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLOR.textMuted,
};

export const modalBodyStyle: CSSProperties = {
  padding: '16px',
  overflowY: 'auto',
  flex: 1,
};

export const modalPlanNameStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  fontWeight: 500,
  color: COLOR.textPrimary,
  margin: '0 0 12px 0',
};

export const modalDescriptionStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  color: COLOR.textBody,
  lineHeight: '1.6',
  whiteSpace: 'pre-wrap',
  margin: 0,
};

export const modalFooterStyle: CSSProperties = {
  padding: '16px',
  backgroundColor: COLOR.white,
  borderTop: `1px solid ${COLOR.border}`,
  display: 'flex',
  justifyContent: 'flex-end',
  borderRadius: '0 0 8px 8px',
};

/* ═══════════════════════════════════════════════════════
   StatusFilterDropdown
   ═══════════════════════════════════════════════════════ */

export const filterDropdownWrapperStyle: CSSProperties = {
  position: 'relative',
};

export const filterTriggerStyle: CSSProperties = {
  height: 32,
  minWidth: 140,
  width: '100%',
  backgroundColor: COLOR.white,
  border: `1px solid ${COLOR.borderMedium}`,
  borderRadius: 4,
  paddingLeft: 8,
  paddingRight: 32,
  fontFamily,
  fontSize: '14px',
  lineHeight: '20px',
  color: COLOR.textPrimary,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  position: 'relative',
  whiteSpace: 'nowrap',
};

export const filterCaretContainerStyle: CSSProperties = {
  position: 'absolute',
  right: 4,
  top: '50%',
  transform: 'translateY(-50%)',
};

export const filterMenuStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  backgroundColor: COLOR.white,
  borderRadius: 4,
  zIndex: 50,
  minWidth: '100%',
  border: `1px solid ${COLOR.borderMedium}`,
  boxShadow: '8px 0px 16px rgba(0,0,0,0.08), 0px 0px 4px rgba(0,0,0,0.04)',
};

export const filterMenuInnerStyle: CSSProperties = {
  padding: '8px 0',
};

export const filterOptionStyle = (isSelected: boolean): CSSProperties => ({
  width: '100%',
  padding: '8px',
  textAlign: 'left',
  fontFamily,
  fontSize: '14px',
  lineHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  backgroundColor: isSelected ? COLOR.filterOptionSelectedBg : 'transparent',
  color: COLOR.textPrimary,
  border: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const filterIconWrapperStyle: CSSProperties = {
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/* ═══════════════════════════════════════════════════════
   AlertBanner – yellow banner shown after "Check my plan"
   ═══════════════════════════════════════════════════════ */

export const alertBannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: COLOR.alertBannerBg,
  border: `1px solid ${COLOR.alertBannerBorder}`,
  borderRadius: 12,
  padding: '16px 8px',
  margin: '0 8px 8px',
};

export const alertBannerLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

export const alertBannerTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  color: COLOR.alertBannerText,
};

export const alertBannerActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

export const alertBannerLinkStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  color: COLOR.navyDark,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 8px',
};

export const alertBannerHideButtonStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  color: COLOR.navyDark,
  backgroundColor: COLOR.white,
  border: `1px solid ${COLOR.borderMedium}`,
  borderRadius: 4,
  padding: '4px 12px',
  cursor: 'pointer',
  height: 32,
};

/* Requirement row alert indicator (left cell) */
export const requirementAlertIndicatorStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 4,
};

/* Grade header alert badge */
export const gradeAlertBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily,
  fontSize: '12px',
  fontWeight: 500,
  color: COLOR.alertBannerText,
  backgroundColor: COLOR.alertBannerBg,
  borderRadius: 4,
  padding: '2px 6px',
  marginTop: 4,
};

/* ═══════════════════════════════════════════════════════
   Alert popover – appears on hover over flag icon
   (matches prototype CourseCard / RequirementRow / GradeHeader popover)
   ═══════════════════════════════════════════════════════ */
export const alertPopoverStyle: CSSProperties = {
  position: 'fixed',
  zIndex: 9999,
  width: 256,
  backgroundColor: COLOR.white,
  borderRadius: 6,
  border: `1px solid ${COLOR.borderMedium}`,
  boxShadow: '8px 0px 16px rgba(0,0,0,0.08), 0px 0px 4px rgba(0,0,0,0.04)',
};

export const alertPopoverContentStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  padding: 16,
};

export const alertPopoverTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: COLOR.textPrimary,
  margin: '0 0 4px',
};

export const alertPopoverMessageStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: COLOR.textPrimary,
  margin: 0,
};

/* ═══════════════════════════════════════════════════════
   AlertsDrawer – right-side panel
   ═══════════════════════════════════════════════════════ */

export const drawerBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 9998,
};

export const drawerPanelStyle: CSSProperties = {
  position: 'fixed',
  right: 0,
  top: 0,
  height: '100%',
  width: 520,
  maxWidth: '90vw',
  backgroundColor: COLOR.white,
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 9999,
};

export const drawerHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 24,
  borderBottom: `1px solid ${COLOR.border}`,
};

export const drawerTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '20px',
  fontWeight: 700,
  color: COLOR.textPrimary,
  margin: 0,
};

export const drawerSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: COLOR.textMuted,
  margin: '4px 0 0',
};

export const drawerCloseButtonStyle: CSSProperties = {
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderRadius: 4,
};

export const drawerContentStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 24,
};

export const drawerEmptyStyle: CSSProperties = {
  textAlign: 'center',
  paddingTop: 48,
};

export const drawerEmptyTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  color: COLOR.textMuted,
};

export const drawerSectionsContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
};

export const drawerSectionHeadingStyle: CSSProperties = {
  fontFamily,
  fontSize: '13px',
  fontWeight: 600,
  color: COLOR.textMuted,
  letterSpacing: '0.05em',
  marginBottom: 12,
};

export const drawerCardListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

export const drawerCardStyle: CSSProperties = {
  backgroundColor: COLOR.white,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 8,
  padding: 16,
};

export const drawerCardRowStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
};

export const drawerCardFlagStyle: CSSProperties = {
  flexShrink: 0,
  marginTop: 2,
};

export const drawerCardBodyStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const drawerCardTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: '22px',
  color: COLOR.textPrimary,
  margin: '0 0 2px',
};

export const drawerCardSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: COLOR.drawerCardSubtitleText,
  margin: '0 0 8px',
};

/** Amber-colored alert-type label shown between subtitle and message (e.g. "Missing prerequisite"). */
export const drawerCardAlertLabelStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: '#B38C00',
  margin: '0 0 4px',
};

export const drawerCardMessageStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  lineHeight: '20px',
  color: COLOR.textPrimary,
  margin: 0,
};

/** Wrapper for action button(s) inside a drawer card — right-aligned, flex-shrink-0. */
export const drawerCardActionsStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  alignSelf: 'center',
};

export const drawerFooterStyle: CSSProperties = {
  borderTop: `1px solid ${COLOR.border}`,
  padding: 24,
};

export const drawerFooterTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: COLOR.textMuted,
  margin: 0,
};

/* ═══════════════════════════════════════════════════════
   AlertFlagWithPopover – trigger + portal popover
   ═══════════════════════════════════════════════════════ */

export const alertFlagTriggerStyle: CSSProperties = {
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
  flexShrink: 0,
};

export const alertPopoverPortalStyle = (top: number, left: number): CSSProperties => ({
  position: 'fixed',
  top,
  left,
  transform: 'translateX(-50%)',
  zIndex: 10000,
  width: 256,
  backgroundColor: COLOR.white,
  borderRadius: 6,
  border: `1px solid ${COLOR.borderMedium}`,
  boxShadow: '8px 0px 16px rgba(0,0,0,0.08), 0px 0px 4px rgba(0,0,0,0.04)',
});

export const alertPopoverFlagWrapperStyle: CSSProperties = {
  flexShrink: 0,
  marginTop: 2,
};

export const alertPopoverBodyStyle: CSSProperties = {
  flex: 1,
};

/* ── Alert popover action buttons ── */

export const alertPopoverActionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 8,
  padding: '0 16px 12px',
};

/** Base action-button style matching the prototype Button component (h-32, Roboto 14/500). */
const alertPopoverActionButtonBase: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  height: 32,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  borderRadius: 4,
  padding: '0 8px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  width: '100%',
};

/** Secondary variant — white bg, navy text, grey border (prototype "secondary"). */
export const alertPopoverActionButtonSecondaryStyle: CSSProperties = {
  ...alertPopoverActionButtonBase,
  color: COLOR.navyDark,
  backgroundColor: COLOR.white,
  border: `1px solid ${COLOR.borderMedium}`,
};

/** Primary variant — blue bg, white text (prototype "primary"). */
export const alertPopoverActionButtonPrimaryStyle: CSSProperties = {
  ...alertPopoverActionButtonBase,
  color: COLOR.white,
  backgroundColor: '#1162EE',
  border: '1px solid transparent',
};

/** Danger variant — red bg, white text, no border (prototype "danger"). */
export const alertPopoverActionButtonDangerStyle: CSSProperties = {
  ...alertPopoverActionButtonBase,
  color: COLOR.white,
  backgroundColor: '#DC2626',
  border: '1px solid transparent',
};

/* ═══════════════════════════════════════════════════════
   PageHeader – layout helpers
   ═══════════════════════════════════════════════════════ */

export const pageHeaderRowFlexStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

export const statusBadgeWrapperStyle: CSSProperties = {
  marginTop: 4,
};

export const creditProgressWrapperStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

export const chevronRotateStyle = (isOpen: boolean): CSSProperties => ({
  transition: 'transform 0.2s',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  display: 'flex',
});

/* ═══════════════════════════════════════════════════════
   Credit progress dropdown
   ═══════════════════════════════════════════════════════ */

export const creditDropdownPositionStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  marginTop: 8,
  zIndex: 1000,
};

export const creditDropdownCardStyle: CSSProperties = {
  backgroundColor: COLOR.white,
  borderRadius: 8,
  boxShadow: '0px 4px 16px rgba(0,0,0,0.12), 0px 0px 4px rgba(0,0,0,0.04)',
  border: `1px solid ${COLOR.borderMedium}`,
  minWidth: 240,
  padding: 16,
};

export const creditDropdownArrowStyle: CSSProperties = {
  position: 'absolute',
  top: -8,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
  borderBottom: `8px solid ${COLOR.white}`,
};

export const creditDropdownArrowBorderStyle: CSSProperties = {
  position: 'absolute',
  top: -9,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
  borderBottom: `8px solid ${COLOR.borderMedium}`,
  zIndex: -1,
};

export const creditDropdownHeadingStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.textDark,
  margin: '0 0 12px',
};

export const creditDropdownListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export const creditDropdownRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const creditDropdownLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily,
  fontSize: '14px',
  color: COLOR.textBody,
};

export const creditDropdownValueStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.textDark,
};

export const creditDropdownNotPlannedRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: COLOR.creditNotPlannedBg,
  borderRadius: 6,
  padding: '6px 8px',
  margin: '0 -8px',
};

export const creditDropdownNotPlannedLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.creditNotPlannedText,
};

export const creditDropdownNotPlannedValueStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.creditNotPlannedText,
};

export const creditDropdownTotalSectionStyle: CSSProperties = {
  borderTop: `1px solid ${COLOR.border}`,
  paddingTop: 12,
  marginTop: 4,
};

export const creditDropdownTotalLabelStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textDark,
};

export const creditDropdownOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 999,
};

/* ═══════════════════════════════════════════════════════
   RequirementCell / GradeHeaderCell – inline layout helpers
   ═══════════════════════════════════════════════════════ */

export const requirementRowBetweenStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const gradeHeaderActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

/* ═══════════════════════════════════════════════════════
   Alert progress bar override colour
   ═══════════════════════════════════════════════════════ */

export const ALERT_PROGRESS_BAR_COLOR = '#F97316';
export const DRAWER_FLAG_COLOR = '#B38C00';

/* ═══════════════════════════════════════════════════════
   Loading / Error state helpers
   ═══════════════════════════════════════════════════════ */

export const centeredFlexStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
};

export const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  gap: 8,
};

export const errorTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  color: COLOR.textSecondary,
};

/* ═══════════════════════════════════════════════════════
   SpecializationsPopover
   ═══════════════════════════════════════════════════════ */

/** Relative-positioned wrapper around the Specializations button + popover. */
export const specializationsPopoverWrapperStyle: CSSProperties = {
  position: 'relative',
};

/** Absolutely-positioned dropdown panel anchored below the Specializations button.
 *  Matches prototype: width 545px, border #DFE2E2, shadow from design spec. */
export const specializationsPopoverStyle: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: '100%',
  marginTop: 8,
  width: 545,
  backgroundColor: COLOR.white,
  borderRadius: 8,
  border: '1px solid #DFE2E2',
  boxShadow: '0px 4px 16px rgba(0,0,0,0.12), 0px 0px 4px rgba(0,0,0,0.04)',
  // Neon tooltips use z-index 9999 internally; keep this below so tooltips render above.
  zIndex: 9998,
};

/** Full-page transparent overlay that closes the popover on outside click. */
export const specializationsPopoverOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
};

/* ── Header section (title + subtitle, with bottom border) ── */
export const specializationsPopoverSectionHeaderStyle: CSSProperties = {
  padding: '12px 16px',
  borderBottom: `1px solid ${COLOR.border}`,
};

export const specializationsPopoverTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: '#1F2937',
  margin: 0,
};

export const specializationsPopoverSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: '#6B7280',
  margin: '2px 0 0',
};

/* ── List section ── */
export const specializationsPopoverListStyle: CSSProperties = {
  padding: '8px 0',
};

export const specializationsPopoverEmptyStyle: CSSProperties = {
  padding: '24px 16px',
  textAlign: 'center',
  fontFamily,
  fontSize: '14px',
  color: '#6B7280',
};

/* Each specialization row is a full-width button */
export const specializationsPopoverItemBtnStyle: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  textAlign: 'left',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

export const specializationsPopoverItemLeftStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const specializationsPopoverItemFlexStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  minWidth: 0,
  gap: 16,
};

export const specializationsPopoverItemTextsStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const specializationsPopoverLabelRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

export const specializationsPopoverLabelTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: '#6B7280',
};

export const specializationsPopoverValueTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: '#1F2937',
  overflowWrap: 'anywhere',
};

export const specializationsPopoverCreditsStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: '#6B7280',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  width: 130,
  textAlign: 'right',
  marginTop: 5,
};

export const specializationsPopoverDeleteWrapStyle: CSSProperties = {
  width: 24,
  marginLeft: 12,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
};

export const specializationsPopoverDeleteBtnStyle: CSSProperties = {
  padding: 4,
  cursor: 'pointer',
  borderRadius: 4,
  backgroundColor: 'transparent',
  border: 'none',
  color: '#6B7280',
};

/** Ensures remove-specialization dialog sits above the popover panel and its backdrop. */
export const specializationsRemoveModalLayerStyle: CSSProperties = {
  position: 'relative',
  zIndex: 11000,
};

/* ── "Add specialization" CTA footer (below list, with top border) ── */
export const specializationsPopoverCtaFooterStyle: CSSProperties = {
  borderTop: `1px solid ${COLOR.border}`,
  padding: '12px 16px',
};

export const specializationsPopoverAddLinkStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.navyDark,
};

export const specializationsPopoverFormHeaderStyle: CSSProperties = {
  padding: '12px 16px 8px',
  borderBottom: `1px solid ${COLOR.border}`,
};

export const specializationsPopoverFormHeadingStyle: CSSProperties = {
  margin: 0,
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.navyDark,
};

export const specializationsPopoverFormBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
};

export const specializationsPopoverFormFooterStyle: CSSProperties = {
  borderTop: `1px solid ${COLOR.border}`,
  padding: '12px 16px',
};

export const specializationsPopoverFormLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '0.875rem',
  fontFamily,
  fontWeight: 600,
  color: COLOR.navyDark,
};

export const specializationsPopoverFormSelectStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${COLOR.border}`,
  borderRadius: '4px',
  fontSize: '0.875rem',
  fontFamily,
  color: COLOR.navyDark,
  backgroundColor: '#fff',
  cursor: 'pointer',
};

/* ═══════════════════════════════════════════════════════
   AddAreaModal
   ═══════════════════════════════════════════════════════ */

export const addAreaModalBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '8px 0',
};

/* ═══════════════════════════════════════════════════════
   SuggestionsPopover
   ═══════════════════════════════════════════════════════ */

/** Relative-positioned wrapper around the Suggestions button + popover. */
export const suggestionsPopoverWrapperStyle: CSSProperties = {
  position: 'relative',
};

/** Absolutely-positioned dropdown panel anchored below the Suggestions button. */
export const suggestionsPopoverStyle: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: '100%',
  marginTop: 8,
  width: 400,
  backgroundColor: COLOR.white,
  borderRadius: 8,
  border: '1px solid #DFE2E2',
  boxShadow: '0px 4px 16px rgba(0,0,0,0.12), 0px 0px 4px rgba(0,0,0,0.04)',
  zIndex: 50,
};

/** Full-page transparent overlay to close popover on outside click. */
export const suggestionsPopoverOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 49,
};

export const suggestionsPopoverHeaderStyle: CSSProperties = {
  padding: '12px 16px',
  borderBottom: `1px solid ${COLOR.border}`,
};

export const suggestionsPopoverTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: '#1F2937',
  margin: 0,
};

export const suggestionsPopoverSubtitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: '#6B7280',
  margin: '2px 0 0',
};

export const suggestionsPopoverListStyle: CSSProperties = {
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

export const suggestionsPopoverEmptyStyle: CSSProperties = {
  padding: '24px 16px',
  textAlign: 'center',
  fontFamily,
  fontSize: '14px',
  color: '#6B7280',
};

/** Each suggestion card */
export const suggestionsPopoverCardStyle: CSSProperties = {
  border: `1px solid ${COLOR.border}`,
  borderRadius: 8,
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export const suggestionsPopoverCardHeaderStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
};

export const suggestionsPopoverCardTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '13px',
  fontWeight: 600,
  color: '#1F2937',
  margin: 0,
};

export const suggestionsPopoverCardDescStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: '#374151',
  margin: 0,
};

/** Inner course block within the suggestion card */
export const suggestionsPopoverCourseBlockStyle: CSSProperties = {
  border: `1px solid ${COLOR.border}`,
  borderRadius: 6,
  padding: '8px 12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};

export const suggestionsPopoverCourseInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const suggestionsPopoverCourseNameStyle: CSSProperties = {
  fontFamily,
  fontSize: '13px',
  fontWeight: 600,
  color: '#1F2937',
  margin: 0,
};

export const suggestionsPopoverCourseMetaStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: '#6B7280',
  margin: 0,
};

/* ═══════════════════════════════════════════════════════
   Remove Course Modal
   ═══════════════════════════════════════════════════════ */

export const removeCourseModalBodyTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: COLOR.textBody,
  margin: '0 0 16px 0',
};

export const removeCourseModalTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '18px',
  fontWeight: 600,
  color: COLOR.textPrimary,
};

export const removeCourseCardStyle: CSSProperties = {
  background: '#f5f5f5',
  borderRadius: '8px',
  padding: '16px',
  border: '1px solid #e0e0e0',
};

export const removeCourseCardNameStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: COLOR.textPrimary,
  margin: '0 0 4px 0',
};

export const removeCourseCardMetaStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  color: COLOR.textBody,
  margin: 0,
};

/* ═══════════════════════════════════════════════════════
   CourseDetailPanel – right-side panel overlay
   ═══════════════════════════════════════════════════════ */

export const courseDetailOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9998,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
};

export const courseDetailPanelStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: '420px',
  maxWidth: '100vw',
  zIndex: 9999,
  backgroundColor: '#ffffff',
  boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const courseDetailHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '24px 24px 16px',
  borderBottom: '1px solid #E5E7EB',
};

export const courseDetailTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '20px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: 0,
  flex: 1,
  paddingRight: '16px',
};

export const courseDetailCloseStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const courseDetailBodyStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
};

export const courseDetailDescriptionStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  lineHeight: '1.6',
  color: COLOR.textBody,
  margin: '0 0 24px 0',
};

export const courseDetailGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px 32px',
  marginBottom: '24px',
};

export const courseDetailFieldLabelStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 500,
  color: COLOR.textMuted,
  margin: '0 0 4px 0',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
};

export const courseDetailFieldValueStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: 0,
};

export const courseDetailSectionTitleStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: '0 0 12px 0',
  paddingTop: '16px',
  borderTop: '1px solid #E5E7EB',
};

export const courseDetailRelationshipCardStyle: CSSProperties = {
  background: '#F9FAFB',
  borderRadius: '8px',
  padding: '12px 16px',
  border: '1px solid #E5E7EB',
  marginBottom: '8px',
};

export const courseDetailRelationshipNameStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 600,
  color: COLOR.textPrimary,
  margin: '0 0 2px 0',
};

export const courseDetailRelationshipMetaStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  color: COLOR.textMuted,
  margin: 0,
};

/**
 * Stacking order for Manage Course Plan overlays portaled to `document.body`.
 * Requisite modals use Neon’s default dialog layer (~9000); grade SelectField poppers use 9999.
 */
export const MANAGE_COURSE_PLAN_Z_INDEX = {
  courseDetailOverlay: 9998,
  alertsDrawerBackdrop: 9998,
  alertsDrawer: 9999,
  alertPopover: 9999,
  alertPopoverPortal: 10000,
  portaledModal: 10000,
  specializationsRemoveModal: 11000,
} as const;
